import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import { Grid } from '@mui/material';
import { CustomFieldsMapper, CustomFields as CustomFieldsValueObject, ReportTask } from '@nx-smartmonkey/shared/domain';
import { HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';

import iconReportPlace from '../../../../../../../assets/img/icon-report-place.svg';
import BackArrowIcon from '../../../../../../../assets/svg/BackArrowIcon';
import CommentsIcon from '../../../../../../../assets/svg/CommentsIcon';
import CrossIcon from '../../../../../../../assets/svg/CrossIcon';
import ImageIcon from '../../../../../../../assets/svg/ImageIcon';
import SignatureIcon from '../../../../../../../assets/svg/SignatureIcon';
import { Modal } from '../../../../../../../components/Modal';
import { STOP_REPORT_STATUS, STOP_STATUS, STOP_TASK_STATUS } from '../../../../../../../constants/Stops';
import { DriverProjectFieldElement } from '../../../../../../../domain/driversProject/DriversProject';
import { ImageUploadedProps } from '../../../../../../../domain/report/StopReportCreate';
import { Route } from '../../../../../../../domain/route/Route';
import { Stop } from '../../../../../../../domain/stop/Stop';
import { StopTask } from '../../../../../../../domain/stop/StopTask';
import { intl } from '../../../../../../../helpers/IntGlobalProvider';
import InputText from '../../../../../../../react-components/InputText';
import { Text } from '../../../../../../../react-components/Text';
import { useLocationContext } from '../../../../../context/locationContext/context';
import { useRetrieveDriverCredentials } from '../../../../../hooks/useRetrieveDriverCredentials';
import { useRetrieveVehicle } from '../../../../../hooks/useRetrieveVehicle';
import { useCreateStopReport } from '../../../../hooks/useCreateStopReport';
import { StopPillInfo } from '../../../StopPillInfo';
import { ReportFooter, ReportFooterType, StopReportFooter } from '../StopReportFooter';
import { CameraMenu, ServiceImageCameraMenu } from '../components/CameraMenu';
import { ReportFields } from '../components/ReportFields';
import { ReportMap } from '../components/ReportMap';
import { StopReportContainer } from '../components/StopReportContainer';
import { StopReportSignatureBox } from './report-actions/StopReportSignatureBox';
import { ReportCompletePin } from './report-actions/pin/ReportCompletePin';

interface StopCompleteProps {
  stop: Stop;
  selectedRoute: Route;
  reportTasks?: ReportTask[];
  temporalReportTasks?: ReportTask[];
  temporalTasks?: StopTask[];
  onClose: (stopId: string) => void;
  onComplete: (stopId: string | undefined) => void;
}

export const StopComplete = ({
  stop,
  selectedRoute,
  reportTasks,
  temporalReportTasks,
  temporalTasks,
  onClose,
  onComplete,
}: StopCompleteProps) => {
  const { locationState } = useLocationContext();
  const { data: credentials } = useRetrieveDriverCredentials();
  const { data: vehicleData } = useRetrieveVehicle();
  const { run: createReport, success, isLoading } = useCreateStopReport();

  const footer = React.useRef<HTMLDivElement>(null);
  const header = React.useRef<HTMLDivElement>(null);
  const [offsetContentHeight, setOffsetContentHeight] = useState<number>(window.innerHeight);

  const [signature, setSignature] = useState<string | undefined>();
  const [showMandatoryFields, setShowMandatoryFields] = React.useState<boolean>(false);
  const [comments, setComments] = React.useState<string | undefined>();
  const [images, setImages] = React.useState<ImageUploadedProps[]>([]);
  const [enableCamera, setEnableCamera] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [incompleteDialogOpen, setIncompleteDialogOpen] = useState<boolean>(false);
  const [pinDialogOpen, setPinDialogOpen] = useState<boolean>(false);
  const [customFields, setCustomFields] = useState<CustomFieldsValueObject>(CustomFieldsValueObject.create({}));
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false);

  const projectIsObligatory = false;

  useEffect(() => {
    if (success) {
      const nextStopId = selectedRoute.stops.find(
        (stop_find) => stop_find.status === STOP_STATUS.pending && stop.id !== stop_find.id
      )?.id;
      onComplete(nextStopId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  useEffect(() => {
    const footerHeight = footer.current?.offsetHeight || 0;
    const headerHeight = header.current?.offsetHeight || 0;
    setOffsetContentHeight(footerHeight + headerHeight);
  }, []);

  function addData(fieldName: string, fieldValue: number | string | boolean | number[]) {
    if (customFields) {
      customFields.update({ [fieldName]: fieldValue });
      setCustomFields(CustomFieldsValueObject.create(CustomFieldsMapper.toDTO(customFields)));
    } else {
      setCustomFields(CustomFieldsValueObject.create({ [fieldName]: fieldValue }));
    }
  }

  const checkValues = () => {
    const hasComments = comments && comments !== ``;
    const hasSignature = !!signature;
    const hasCustomFields = Object.keys(customFields.value).length > 0;
    const hasImage = images.length > 0;
    return hasComments || hasSignature || hasCustomFields || hasImage;
  };

  const clearAllSets = () => {
    setSignature(undefined);
    setComments(undefined);
    setImages([]);
    setEnableCamera(false);
    setSelectedIndex(-1);
    setIncompleteDialogOpen(false);
    setPinDialogOpen(false);
    setCustomFields(CustomFieldsValueObject.create({}));
    setShowCloseModal(false);
  };
  const handleImagesChange = (newImages: ImageUploadedProps[]) => {
    setImages(newImages);
  };

  if (enableCamera) {
    return (
      <CameraMenu
        enableGallery={vehicleData?.project.gallery_enabled}
        selectedIndexInput={selectedIndex}
        inputImages={images}
        outputImages={handleImagesChange}
        closeCamera={() => setEnableCamera(false)}
        limitImages={5}
      />
    );
  }

  const isDriverPositionMandatory = vehicleData?.project.fields.configuration?.includes(`driver_position`);

  const requiredCustomFields = vehicleData?.project
    ? vehicleData.project.fields.stop_completed_fields.filter((field) => field.is_required && field.is_custom_field)
    : [];
  const requiredNotCustomFields = vehicleData?.project
    ? vehicleData.project.fields.stop_completed_fields.filter((field) => field.is_required && !field.is_custom_field)
    : [];

  const checkRequiredFields = (field_name: string) => {
    if (field_name === `signature`) {
      return signature !== undefined;
    }
    if (field_name === `comments`) {
      return comments && comments !== ``;
    }
    if (field_name === `images`) {
      return images.length > 0;
    }
    return false;
  };

  const remainingRequiredCustomFields = requiredCustomFields.filter((field) => {
    return customFields.value[field.id] === undefined;
  });

  const remainingRequiredNotCustomFields = requiredNotCustomFields.filter((field) => {
    return !checkRequiredFields(field.id);
  });

  const onCompleteReport = async (pin?: string) => {
    if (isLoading || success || stop.status !== STOP_STATUS.pending) {
      return;
    }

    // Check if all the required fields are filled
    if (
      remainingRequiredCustomFields.length > 0 ||
      remainingRequiredNotCustomFields.length > 0 ||
      (isDriverPositionMandatory && locationState.currentPosition === undefined)
    ) {
      setShowMandatoryFields(true);
      return;
    }

    // Check if there are pending tasks
    const hasPendingTasks =
      vehicleData?.project.tasks_enabled &&
      (reportTasks
        ? reportTasks.some((reportTask) => reportTask.status === STOP_TASK_STATUS.pending)
        : false || temporalReportTasks
        ? temporalReportTasks.some((reportTask) => reportTask.status === STOP_TASK_STATUS.pending)
        : false);

    if (vehicleData?.project.tasks_enabled && hasPendingTasks && !incompleteDialogOpen && !pinDialogOpen) {
      setIncompleteDialogOpen(true);
      return;
    }

    setIncompleteDialogOpen(false);

    if (stop.type !== `pickup` && vehicleData?.project.pin_enabled && !pinDialogOpen && !pin) {
      setPinDialogOpen(true);
      return;
    }

    setPinDialogOpen(false);

    const allReportTasks = [...(reportTasks ?? []), ...(temporalReportTasks ?? [])] as ReportTask[];

    const status =
      allReportTasks.length > 0 && allReportTasks.some((task) => task.status !== STOP_REPORT_STATUS.completed)
        ? STOP_REPORT_STATUS.incomplete
        : STOP_REPORT_STATUS.completed;

    createReport({
      status,
      stop: stop!,
      reportData: {
        comments: comments && comments !== `` ? comments : undefined,
        signature: signature || undefined,
        customFields,
        pin,
        reportTasks: reportTasks ?? [],
        temporalReportTasks: temporalReportTasks ?? [],
        temporalTasks: temporalTasks ?? [],
      },
      images: images.filter((image) => !!image.data),
    });
  };

  if (
    vehicleData?.project.fields.stop_completed_fields.length === 0 &&
    stop.getPendingTasks().length === 0 &&
    !credentials?.test(`can_update_stop_tasks`)
  ) {
    if (stop.type !== `pickup` && vehicleData?.project.pin_enabled) {
      return (
        <ReportCompletePin
          isLoading={isLoading}
          open={Boolean(stop)}
          stop={stop!}
          onConfirm={(pin: string) => {
            onCompleteReport(pin);
          }}
          onCancel={() => onClose(stop.id)}
        />
      );
    }

    onCompleteReport();

    return null;
  }

  return (
    <>
      <Modal
        open={showCloseModal}
        title={{
          intlId: `drivers.stop.report.modal.title`,
        }}
        subtitle={{
          intlId: `drivers.stop.report.modal.subtitle`,
        }}
        buttons={[
          {
            intlId: `drivers.stop.report.modal.leave`,
            onClick: () => {
              setShowCloseModal(false);
              onClose(stop.id);
              clearAllSets();
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setShowCloseModal(false);
            },
          },
        ]}
        children={<VSpacer medium />}
      />
      <Grid
        container
        style={{
          position: `absolute`,
          inset: 0,
          display: `flex`,
          alignContent: `flex-start`,
          backgroundColor: RoutalPalette.white,
          zIndex: 1003,
        }}
      >
        {/* HEADER */}
        <Grid
          ref={header}
          style={{
            display: `flex`,
            flexDirection: `row`,
            flexWrap: `nowrap`,
            width: `100%`,
            height: `fit-content`,
            justifyContent: `space-between`,
            alignItems: `center`,
            padding: `10px 20px 0px 20px`,
            borderBottom: `1px solid ${RoutalPalette.neutral10}`,
          }}
        >
          <StopPillInfo
            label={stop.location?.label}
            subLabel={stop.label}
            id={stop.id}
            stopOrder={stop.order + 1}
            maxStops={selectedRoute.stops.length}
          />
          {vehicleData?.project.tasks_enabled &&
          ((reportTasks && reportTasks.length > 0) || (temporalReportTasks && temporalReportTasks.length > 0)) ? (
            <BackArrowIcon
              style={{
                color: RoutalPalette.secondary40,
                height: `28px`,
                width: `28px`,
                fontSize: `25px`,
                cursor: `pointer`,
              }}
              onClick={(e: any) => {
                if (!isLoading) {
                  if (checkValues()) {
                    setShowCloseModal(true);
                  } else {
                    onClose(stop.id);
                  }
                }
              }}
            />
          ) : (
            <CrossIcon
              style={{
                color: RoutalPalette.secondary40,
                height: `20px`,
                width: `20px`,
                fontSize: `25px`,
                cursor: `pointer`,
              }}
              onClick={(e: any) => {
                if (!isLoading) {
                  if (checkValues()) {
                    setShowCloseModal(true);
                  } else {
                    onClose(stop.id);
                  }
                }
              }}
            />
          )}
        </Grid>

        {/* CONTENT */}
        <Grid
          container
          style={{
            height: `calc(100% - ${offsetContentHeight}px`,
            width: `100%`,
          }}
        >
          <Grid
            container
            direction="column"
            justifyContent="space-between"
            display="block"
            wrap="nowrap"
            style={{
              // minHeight: `${window.innerHeight - 130}px`,
              height: `100%`,
              pointerEvents: isLoading ? `none` : undefined,
              flexDirection: `column`,
              // flexWrap: `nowrap`,
              overflowY: `auto`,
              overflowX: `hidden`,
              padding: `8px 20px 8px 20px`,
            }}
          >
            <VSpacer medium />
            <StopReportContainer
              textId="drivers.stop.report.location"
              showMandatory={showMandatoryFields}
              isMandatory={vehicleData?.project.fields.configuration.includes(`driver_position`)}
              childrenImage={<img alt="Report" src={iconReportPlace} style={{ height: `15px`, width: `20px` }} />}
              hasValue={true}
            >
              <ReportMap
                selectedRoute={selectedRoute}
                showMandatory={isDriverPositionMandatory && locationState.currentPosition === undefined}
              />
            </StopReportContainer>

            <VSpacer medium />

            {vehicleData?.project.fields?.stop_completed_fields?.map(
              (field: DriverProjectFieldElement, index: number) => {
                if (field.is_custom_field) {
                  return (
                    <ReportFields
                      customFields={customFields}
                      addData={addData}
                      fieldElement={field}
                      position={index}
                      showMandatory={showMandatoryFields}
                    />
                  );
                }

                switch (field.id) {
                  case `signature`:
                    return (
                      <>
                        <StopReportContainer
                          textId="driver.stop.report.signature"
                          showMandatory={showMandatoryFields}
                          isMandatory={field.is_required}
                          childrenImage={<SignatureIcon style={{ color: RoutalPalette.grey7 }} />}
                          hasValue={!!signature}
                        >
                          <StopReportSignatureBox
                            onUpdateSignature={(sign) => setSignature(sign)}
                            signature={signature}
                          />
                        </StopReportContainer>
                        <VSpacer medium />
                      </>
                    );
                  case `images`:
                    return (
                      <>
                        <StopReportContainer
                          textId="drivers.stop.report.images"
                          showMandatory={showMandatoryFields}
                          isMandatory={field.is_required}
                          childrenImage={
                            <ImageIcon style={{ height: `15px`, width: `20px`, color: RoutalPalette.secondary00 }} />
                          }
                          hasValue={images.length > 0}
                        >
                          <Grid
                            style={{
                              height: showMandatoryFields && projectIsObligatory && images.length <= 0 ? 24 : 0,
                              transition: `height 0.3s, opacity 0.3s`,
                              opacity: showMandatoryFields && projectIsObligatory && images.length <= 0 ? 1 : 0,
                            }}
                          >
                            {showMandatoryFields && projectIsObligatory && images.length <= 0 && (
                              <>
                                <Text
                                  variant="label"
                                  intlId={`drivers.stop.report.mandatory_field`}
                                  style={{ color: RoutalPalette.error.medium }}
                                />
                                <VSpacer small />
                              </>
                            )}
                          </Grid>
                          {images.length > 0 && (
                            <>
                              <Grid
                                container
                                direction="row"
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                  gap: `10px`,
                                  display: `flex`,
                                  flexWrap: `nowrap`,
                                  alignItems: `center`,
                                  overflowX: `auto`,
                                  overflowY: `hidden`,
                                  height: `100px`,
                                }}
                              >
                                {images.map((image: ImageUploadedProps, index: number) => {
                                  return (
                                    <div key={index} style={{ height: `100px` }}>
                                      <ServiceImageCameraMenu
                                        imageData={image.data}
                                        style={{ height: `100px`, aspectRatio: `9/16`, objectFit: `cover` }}
                                        onClick={() => {
                                          setSelectedIndex(index);
                                          setEnableCamera(true);
                                        }}
                                      />
                                    </div>
                                  );
                                })}
                              </Grid>
                              <VSpacer medium />
                            </>
                          )}
                          <Grid
                            item
                            container
                            direction="row"
                            alignItems="center"
                            style={{
                              height: `50px`,
                              borderRadius: `10px`,
                              backgroundColor: RoutalPalette.primary40,
                              borderColor: RoutalPalette.primary40,
                              display: `flex`,
                              flexDirection: `row`,
                              flexWrap: `nowrap`,
                              justifyContent: `center`,
                              alignItems: `center`,
                            }}
                            onClick={(e: any) => {
                              setEnableCamera(!enableCamera);
                            }}
                          >
                            <AddAPhotoOutlinedIcon style={{ fill: RoutalPalette.white }} />
                            <HSpacer small />
                            <Text intlId={`driver.stop.report.add_images`} style={{ color: RoutalPalette.white }} />
                          </Grid>
                        </StopReportContainer>
                        <VSpacer medium />
                      </>
                    );
                  case `comments`:
                    return (
                      <>
                        <StopReportContainer
                          textId="driver.stop.report.tasks.comments"
                          childrenImage={<CommentsIcon alt="iconComment" style={{ height: `15px`, width: `20px` }} />}
                          showMandatory={showMandatoryFields}
                          isMandatory={field.is_required}
                          hasValue={!!comments && comments !== ``}
                        >
                          <Grid
                            style={{
                              height: showMandatoryFields && projectIsObligatory && !comments ? 24 : 0,
                              transition: `height 0.3s, opacity 0.3s`,
                              opacity: showMandatoryFields && projectIsObligatory && !comments ? 1 : 0,
                            }}
                          >
                            {showMandatoryFields && projectIsObligatory && !comments && (
                              <>
                                <Text
                                  variant="label"
                                  intlId={`drivers.stop.report.mandatory_field`}
                                  style={{ color: RoutalPalette.error.medium }}
                                />
                                <VSpacer small />
                              </>
                            )}
                          </Grid>
                          <InputText
                            multiline
                            placeholder={intl.formatMessage({ id: `driver.stop.report.comments_ph` })}
                            rowsMin={4}
                            fieldValue={comments || ``}
                            onChange={(value) => setComments(value)}
                            variant="outlined"
                            inputContainerStyles={{ width: `100%` }}
                            inputStyles={{
                              width: `100%`,
                              padding: `16.5px 14px`,
                              borderColor: RoutalPalette.grey7,
                            }}
                          />
                        </StopReportContainer>
                        <VSpacer medium />
                      </>
                    );
                  default:
                    return (
                      <ReportFields
                        addData={addData}
                        fieldElement={field}
                        position={index}
                        showMandatory={showMandatoryFields}
                      />
                    );
                }
              }
            )}
          </Grid>
        </Grid>

        {/* FOOTER */}
        <Grid ref={footer} style={{ height: `fit-content`, position: `absolute`, bottom: 0, left: 0, right: 0 }}>
          <StopReportFooter
            onClick={onCompleteReport}
            report={ReportFooter.complete as ReportFooterType}
            isLoading={isLoading || success}
          />
        </Grid>
      </Grid>
      <ReportCompletePin
        isLoading={isLoading}
        open={pinDialogOpen && Boolean(stop)}
        stop={stop!}
        onConfirm={(pin: string) => {
          onCompleteReport(pin);
        }}
        onCancel={() => setPinDialogOpen(false)}
      />
    </>
  );
};
